import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useContext,
} from "react";
import Aggrid from "../../../framework/AgGrid";
import classes from "./index.module.css";
import moment from "moment";
import CellNumberEditor from "../../../framework/cellEditor/CellNumberEditor";
import GlobalContext from "../../../framework/GlobalContext";
import { selectRow, rowDown, rowUp } from "../../../utils/gridUtils";
import { fetchFrequencySlabDetails } from "../../../services/frequency-slab";

const FrequencySlabDetails = (props) => {
  const { frequencySlabCode = null, onEscape = () => {} } = props;
  const [frequencyData, setFrequencyData] = useState(null);
  const [rowData, setRowData] = useState(null);
  const [api, setApi] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const { currentComponent, setCurrentComponent } = useContext(GlobalContext);

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "From Frequency",
        field: "min",
        width: 200,
      },
      {
        headerName: "To Frequency",
        field: "max",
        width: 200,
      },
      {
        headerName: "Min Days",
        field: "minDays",
        width: 200,
      },
      {
        headerName: "Max Days",
        field: "maxDays",
        width: 200,
      },
    ];
  }, []);

  const getFrequencySlabDetails = useCallback((frequencySlabCode) => {
    fetchFrequencySlabDetails(`_id=${frequencySlabCode}`).then((resp) => {
      const data = resp?.result?.rows;
      setFrequencyData(data);
      const updatedRowData = data?.frequencyProgramDetails?.map((item) => ({
        min: item.frequency?.min,
        max: item.frequency?.max,
        minDays: item.days?.min,
        maxDays: item.days?.max,
      }));
      setRowData(updatedRowData);
    });
  }, []);

  const onGridReadyHandler = useCallback(
    (params) => {
      setApi(params.api);
      params.api.sizeColumnsToFit();
      selectRow(params.api, selectedRowIndex);
    },
    [selectedRowIndex]
  );

  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        onEscape();
        return;
      }
      if (key === "arrowdown") {
        rowDown(api);
        return;
      }
      if (key === "arrowup") {
        rowUp(api);
        return;
      }
    },
    [api, onEscape]
  );

  useEffect(() => {
    if (frequencySlabCode) {
      getFrequencySlabDetails(frequencySlabCode);
    }
  }, [getFrequencySlabDetails, frequencySlabCode]);

  useEffect(() => {
    setCurrentComponent("grid_frequency_slab_details");
  }, [setCurrentComponent]);

  useEffect(() => {
    if (currentComponent === "grid_frequency_slab_details") {
      document.addEventListener("keyup", keyupListener, false);
    }
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [api, currentComponent, keyupListener]);

  return (
    <div className={classes["frequency-slab-details"]}>
      <div className={classes["frequency-slab-details-header"]}>
        <div className={classes["frequency-slab-details-header-left"]}>
          <span>
            {frequencyData?.frequencyId} -{" "}
            {frequencyData?.programName?.toUpperCase()}{" "}
            {frequencyData?.frequencySlabCode}
          </span>
          <span>Company - {frequencyData?.company}</span>
          <span>
            Created Date and Time -{" "}
            {moment(`${frequencyData?.createdTime}`).format(
              "DD/MM/YYYY hh:mm A"
            )}
          </span>
        </div>
      </div>

      <div className={classes["frequency-slab-details-grid"]}>
        <Aggrid
          id="grid_frequency_slab_details"
          className="ag-theme-balham"
          rowData={rowData}
          columnDefs={columnDefs}
          frameworkComponents={{
            cellNumberEditor: CellNumberEditor,
          }}
          defaultColDef={{
            resizable: true,
            cellStyle: (params) => {
              return {
                borderRight: "1px solid #a0a0a0",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              };
            },
            suppressKeyboardEvent: (params) => true,
          }}
          onGridReady={(params) => {
            setTimeout(() => {
              onGridReadyHandler(params);
            }, 100);
          }}
          onCellClicked={(params) => {
            setSelectedRowIndex(params.rowIndex);
          }}
        />
      </div>
    </div>
  );
};

export default FrequencySlabDetails;
